import { useState } from 'react'

import { GAMES, TRIES_PER_QUESTION, type MathsView, type SkillLevels } from '@hazel/shared'

import { useBarTitle } from '../layouts/bar-title.js'
import { digitFrom, useGameKeys } from './useGameKeys.js'
import { Card } from '../components/Card.js'
import { Announce } from './Announce.js'
import { useRoundFocus } from './useRoundFocus.js'
import { useLeaveOnEscape } from './useLeaveOnEscape.js'
import { LeaveGame } from './LeaveGame.js'
import { KeyLegend } from '../components/KeyLegend.js'
import { LEAVE_SHORTCUT } from '../components/shortcuts.js'
import { Milestone } from './Milestone.js'
import { PlayAgain } from './PlayAgain.js'
import { PlayColumns } from './PlayColumns.js'
import { RaceTrack } from './RaceTrack.js'
import { LANE, RACE_ART, racerPicture } from './raceArt.js'
import { MAX_DIGITS, NumberPad } from './NumberPad.js'
import { useMaths } from './useMaths.js'
import { useRound, type DealtRound } from './useRound.js'
import { useAnswerCues, useRoundEndCues, useRoundMusic } from './gameSound.js'
import { TRACK_FOR_GAME } from '../sound/cues.js'

/**
 * What to say after an answer.
 *
 * A wrong one with goes left is an invitation, not a verdict: the question
 * stays up and she tries again. Only once the goes are used up is the answer
 * shown — calmly, and without "wrong" anywhere in it.
 */
function feedbackFor(view: MathsView): string {
  const answer = view.lastAnswer
  if (!answer) return ''

  if (answer.correct) return 'Yes!'
  if (answer.moreGoes) return 'Not quite — try again.'

  return `${answer.prompt} is ${answer.expected}. We'll come back to it.`
}

export function Maths() {
  // The bar carries the name while she is here, in place of "Hazel's
  // Games". One call rather than one per screen: the chooser and the
  // board are both this component, so the name does not blink out between
  // picking a size and being dealt one.
  useBarTitle(GAMES.maths.title)

  /*
    The screen's music, not the round's.

    On the round it started when play did — so Sky's was silent through "How
    big a board?", and every game had a beat of nothing while its round was
    being dealt. Asking here means it also survives another go, which is the
    same screen and should not restart the track.
  */
  useRoundMusic(TRACK_FOR_GAME.maths)

  const { round, again, failed } = useRound<{ levels: SkillLevels }>({ game: 'maths' })

  if (failed) {
    return (
      <p role="alert" className="text-coral py-16 text-center">
        We couldn’t get your questions just now. Have another go in a moment.
      </p>
    )
  }

  // Waits for the round before dealing, so the questions are at the right
  // difficulty from the first one rather than being swapped out. The levels
  // come with it, which is what stops them drifting from what the server will
  // check the round against.
  if (!round) {
    return (
      <p role="status" className="py-16 text-center">
        Getting your questions ready…
      </p>
    )
  }

  // Keyed on the seed, so another go is a fresh round.
  return <MathsRound key={round.seed} round={round} onAgain={again} />
}

function MathsRound({
  round,
  onAgain,
}: {
  round: DealtRound<{ levels: SkillLevels }>
  onAgain: () => void
}) {
  const leaveOnEscape = useLeaveOnEscape()
  const game = useMaths(round, onAgain)
  const [typed, setTyped] = useState('')

  const { view } = game
  const board = useRoundFocus<HTMLDivElement>(round.seed, view.status !== 'playing')

  useAnswerCues(view)
  useRoundEndCues(view.status !== 'playing', game.milestone)

  function submit(): void {
    if (typed === '') return
    game.answer(Number.parseInt(typed, 10))
    setTyped('')
  }

  // Typed as well as tapped. On a television with a keyboard there is no way to
  // reach the number pad, and hunting for it with arrow keys would be a poor
  // substitute for just typing the answer.
  useGameKeys((event) => {
    // Before the status gate: a finished round is exactly when somebody wants
    // out, and there is nothing left to clear once it is over.
    if (event.key === 'Escape' && view.status !== 'playing') {
      return void leaveOnEscape(false)
    }

    if (view.status !== 'playing') {
      if (event.key === 'Enter') game.again()
      return
    }

    const digit = digitFrom(event)
    if (digit) {
      // Capped at the pad's own limit, so a lean on the 7 key cannot grow a
      // number wider than the box that shows it.
      setTyped((current) => (current.length >= MAX_DIGITS ? current : current + digit))
      return
    }

    if (event.key === 'Enter') return submit()
    if (event.key === 'Backspace') return setTyped((current) => current.slice(0, -1))
    if (event.key === 'Escape') {
      // Clears the answer, or leaves the game when there is nothing to clear.
      if (leaveOnEscape(typed !== '')) return
      return setTyped('')
    }
  })

  return (
    <div className="mx-auto flex w-full max-w-xl [[data-scale=tv]_&]:max-w-none flex-col items-center gap-4">
      <Card
        ref={board}
        tabIndex={-1}
        className="outline-none flex w-full flex-col items-center gap-4"
      >
        {/* Progress, so the end is in sight rather than open-ended. Not
            announced: it changes on every answer, and hearing the score
            recited between every question is noise, not information. */}
        <p className="text-ink-muted text-(length:--text-small)">
          {`${view.correct} of ${view.roundLength} done`}
        </p>

        {/* Feedback first, then the question — the order they are read on
            screen, and fixed rather than left to whichever region updates
            first. Answering changes both in the same tick. */}
        <Announce>
          {[
            view.status !== 'playing'
              ? `${view.correct} out of ${view.roundLength} right.`
              : feedbackFor(view),
            view.status === 'playing' ? `Question: ${view.prompt ?? ''}` : '',
            game.milestone ? `That’s ${game.milestone} games played.` : '',
          ]
            .filter(Boolean)
            .join(' ')}
        </Announce>

        {/*
          Monkey, walking to a banana.

          One step per question she gets right, out of the ten the round is.
          A wrong answer does not send him back — it simply does not move him,
          which is the difference between a game that is hard and one that is
          punishing.
        */}
        <RaceTrack
          goal={RACE_ART.banana}
          racers={[
            {
              key: 'monkey',
              name: 'Monkey',
              picture: racerPicture('monkey'),
              steps: view.correct,
              of: view.roundLength,
              tint: LANE.monkey,
            },
            {
              key: 'daddy',
              name: 'Daddy',
              picture: RACE_ART.daddy,
              steps: view.wrongAnswers,
              // Three times the round, so three mistakes cost the ground one
              // right answer gains. It has to be possible to get things wrong
              // and still win, or the race stops being encouraging and becomes
              // a verdict — but a round of pure guessing still loses.
              of: view.roundLength * TRIES_PER_QUESTION,
              tint: LANE.daddy,
            },
          ]}
        />

        {view.status === 'playing' ? (
          <PlayColumns
            reading={
              <div className="flex flex-col items-center gap-3">
                {/* Shown after each answer, so a wrong one teaches rather than
                  just being wrong. No scolding: the right answer, calmly. */}
                {view.lastAnswer ? (
                  <p
                    className={`[[data-scale=tv]_&]:text-(length:--text-heading) ${view.lastAnswer.correct ? 'text-teal' : 'text-ink-muted'}`}
                  >
                    {feedbackFor(view)}
                  </p>
                ) : null}

                {/*
                  The sum and the box it is answered into, on one line.

                  "7 × 8 = ▢" is how a question is written down everywhere else
                  in her life; a sum with a separate box underneath is a form.

                  At every size, not only the television. It is how the
                  question reads, not how much room there is — and a phone has
                  the least reason of all to spend a line on a box.
                  The equals sign is hidden from a screen reader — the question
                  carries its own label and the box says what it is.

                  A non-breaking space when there is nothing typed, not a plain
                  one: a plain space at the end of an inline box is collapsed
                  away, so an empty box has no line in it and stands shorter
                  than a full one.
                */}
                <div className="font-display flex flex-wrap items-center justify-center gap-3 text-(length:--text-hero)">
                  {/*
                    The label wraps the sum and nothing else.
                    
                    It used to wrap the whole line, which put the equals sign
                    and the box inside it — so anything reading the question's
                    text got "6 − 5=" and could not work out the answer. The
                    label names the question; the box has a label of its own.
                  */}
                  <p aria-label={`Question: ${view.prompt ?? ''}`}>{view.prompt}</p>

                  <span aria-hidden="true">=</span>

                  <span
                    className="border-border min-h-14 min-w-32 rounded-(--radius-control) border-2 px-4 tabular-nums"
                    aria-label={typed === '' ? 'No answer yet' : `Your answer: ${typed}`}
                  >
                    {typed || '\u00a0'}
                  </span>
                </div>
              </div>
            }
            answering={<NumberPad value={typed} onChange={setTyped} onSubmit={submit} />}
            split="mostly-reading"
          />
        ) : (
          <div className="flex flex-col items-center gap-3">
            {/* A round can also end by running out of tries, and congratulating
                her on a full round she did not finish would be a small lie she
                would notice. */}
            <p className="font-display text-(length:--text-title)">
              {view.correct === view.roundLength ? 'All done!' : 'Good effort!'}
            </p>
            <p className="text-ink-muted [[data-scale=tv]_&]:text-(length:--text-heading)">{`${view.correct} out of ${view.roundLength} right.`}</p>

            {game.milestone ? <Milestone games={game.milestone} /> : null}

            <PlayAgain
              label="Another go"
              does="to play again"
              onAgain={() => {
                setTyped('')
                game.again()
              }}
            />
          </div>
        )}
      </Card>

      <LeaveGame />

      <KeyLegend
        shortcuts={
          view.status === 'playing'
            ? [
                { keys: ['0', '–', '9'], does: 'Type your answer' },
                { keys: ['Enter'], does: 'Send it' },
                { keys: ['Bksp'], does: 'Fix a digit' },
                LEAVE_SHORTCUT,
              ]
            : [{ keys: ['Enter'], does: 'Another go' }, LEAVE_SHORTCUT]
        }
      />
    </div>
  )
}
